/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { GridApi } from 'ag-grid-community';
import { Subscription } from 'rxjs';

import { GridOptionsExt } from '../ext/grid-options-ext';
import {
  GRID_FEATURE,
  GridFeature,
  GridFeatureInitParams,
  GridFeatureReadyParams,
} from '../feature.interfaces';
import { GridViewportRange, GridViewportState } from './grid-viewport.interfaces';
import { ImmutableGridViewport } from './immutable-grid-viewport';

export const EMPTY_GRID_VIEWPORT_STATE = { rowCount: -1, rowData: [], loading: false, range: { start: 0, end: -1 } };

@Directive({
  standalone: false,
  selector: '[ghGridViewportState]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridViewportStateDirective,
    multi: true,
  }],
})
export class GridViewportStateDirective implements GridFeature {
  @Output() viewportChanged = new EventEmitter<GridViewportRange>();

  private _state: GridViewportState<any> = EMPTY_GRID_VIEWPORT_STATE;
  private initialized = false;
  private api: GridApi;
  private viewportDs: ImmutableGridViewport<any>;
  private rangeSub: Subscription;

  @Input('ghGridViewportState')
  set state(state: GridViewportState<any>) {
    if (this.initialized) {
      this.applyState(state);
    } else {
      this._state = state;
    }
  }

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  enhanceGridOptions(options: GridOptionsExt): void {
    options.rowModelType = 'viewport';
    this.viewportDs = new ImmutableGridViewport();
    options.viewportDatasource = this.viewportDs;
    options.viewport = this.viewportDs;
  }

  onGridReady(params: GridFeatureReadyParams): void {
    const { api } = params;
    this.initialized = true;
    this.api = api;

    const state = this._state;
    this._state = EMPTY_GRID_VIEWPORT_STATE;
    this.state = state;

    this.rangeSub = this.viewportDs.range.subscribe((range) => this.viewportChanged.emit(range));
  }

  onDestroy(): void {
    this.rangeSub?.unsubscribe();
    this.viewportDs?.disconnect();
  }

  private applyState(state: GridViewportState<any>): void {
    const oldState = this._state;
    const { api } = this;

    if (oldState.loading !== state.loading) {
      /** v32+: use grid option `loading` instead of showLoadingOverlay/hideOverlay. */
      api.setGridOption('loading', state.loading);
    }
    if (oldState.rowCount !== state.rowCount) {
      this.viewportDs.setRowCount(state.rowCount);
    }
    if (oldState.rowData !== state.rowData) {
      this.viewportDs.setRowData(state.rowData);
    }

    this._state = state;
  }
}
